import React from 'react';
import { getTextColor, lightenColor } from '../../../utils/colorUtils';
import { useLanguage } from '../../../contexts/LanguageContext';
import './Tags.scss';

export const Tags = ({
    tags = [],
    activeTags = new Set(),
    onTagToggle,
    loading = false,
    interactive = true,
    mode = 'toggle', // 'toggle', 'display', 'edit'
    maxDisplayed = null,
    className = '',
    size = 'medium', // 'small', 'medium', 'large'
    variant = 'default' // 'default', 'minimal', 'outlined'
}) => {
    const { t } = useLanguage();

    if (loading) {
        return (
            <div className={`tags-loading ${className}`}>
                <span>{t('common.loading')}...</span>
            </div>
        );
    }

    if (!tags || tags.length === 0) {
        return (
            <div className={`no-tags ${className}`}>
                <span>{t('resourceView.noTagsAvailable')}</span>
            </div>
        );
    }

    const displayedTags = maxDisplayed ? tags.slice(0, maxDisplayed) : tags;
    const remainingCount = maxDisplayed && tags.length > maxDisplayed ? tags.length - maxDisplayed : 0;

    const handleTagClick = (tagName) => {
        if (!interactive || !onTagToggle) return;
        onTagToggle(tagName);
    };

    const getTagStyle = (tag, isActive) => {
        const tagColor = typeof tag === 'object' ? tag.color : null;

        if (!tagColor) return {};

        if (isActive && interactive) {
            return {
                backgroundColor: tagColor,
                borderColor: tagColor,
                color: getTextColor(tagColor),
                boxShadow: `0 2px 8px ${tagColor}40`
            };
        } else if (interactive) {
            return {
                '--tag-color': tagColor,
                '--tag-hover-color': lightenColor(tagColor, 30),
                '--tag-hover-text-color': getTextColor(lightenColor(tagColor, 30))
            };
        } else {
            // Display mode - show with color but no hover effects
            return {
                backgroundColor: tagColor,
                borderColor: tagColor,
                color: getTextColor(tagColor)
            };
        }
    };

    return (
        <div className={`tags-container ${className}`}>
            <div className={`tags-list tags-list--${size} tags-list--${variant}`}>
                {displayedTags.map((tag, index) => {
                    const tagName = typeof tag === 'string' ? tag : tag.name;
                    const tagColor = typeof tag === 'object' ? tag.color : null;
                    const isActive = activeTags.has(tagName);

                    const tagClasses = [
                        'tag',
                        interactive ? (isActive ? 'tag-active' : 'tag-inactive') : 'tag-display',
                        tagColor ? 'tag-colored' : '',
                        interactive ? 'tag-interactive' : 'tag-static'
                    ].filter(Boolean).join(' ');

                    const tagStyle = getTagStyle(tag, isActive);

                    if (interactive) {
                        return (
                            <button
                                key={tag.id || index}
                                className={tagClasses}
                                onClick={() => handleTagClick(tagName)}
                                type="button"
                                style={tagStyle}
                                title={typeof tag === 'object' && tag.description ? tag.description : tagName}
                            >
                                {tagName}
                            </button>
                        );
                    } else {
                        return (
                            <span
                                key={tag.id || index}
                                className={tagClasses}
                                style={tagStyle}
                                title={typeof tag === 'object' && tag.description ? tag.description : tagName}
                            >
                                {tagName}
                            </span>
                        );
                    }
                })}

                {remainingCount > 0 && (
                    <span className="tag tag-more">
                        +{remainingCount}
                    </span>
                )}
            </div>
        </div>
    );
};

export default Tags;
